import { useState, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

const manualTourDef: SlotGroupDefinition = {
  label: 'Featured Tour',
  fields: {
    url: { label: 'Embed URL', type: 'url', defaultValue: '' },
    title: { label: 'Title', type: 'text', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const manualTour = useSlotGroup('virtualtourembed.manual_tour', manualTourDef);
  useComfyCtaStyles();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(() => {
    const collectionTours = (tours || []).filter((t) => t.virtual_tour_url);
    if (manualTour.url) {
      return [
        {
          virtual_tour_title: manualTour.title || 'Featured Tour',
          virtual_tour_url: manualTour.url,
        },
        ...collectionTours,
      ];
    }
    return collectionTours;
  }, [tours, manualTour.url, manualTour.title]);

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="flex justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <span className="vt-tour-select relative inline-block max-w-full">
            <select
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(Number(e.target.value))}
              className="comfy-cta vt-tour-select__control max-w-full"
              aria-label="Select a virtual tour"
            >
              {validTours.map((tour, i) => (
                <option key={i} value={i}>
                  {tour.virtual_tour_title || `Tour ${i + 1}`}
                </option>
              ))}
            </select>
            <span className="vt-tour-select__caret" aria-hidden="true" />
          </span>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableSlotGroup groupPath="virtualtourembed.manual_tour" definition={manualTourDef}>
          {activeTour?.virtual_tour_url ? (
            <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
              <iframe
                key={activeTour.virtual_tour_url}
                src={activeTour.virtual_tour_url}
                className="absolute inset-0 w-full h-full border-0"
                allowFullScreen
                title={activeTour.virtual_tour_title || 'Virtual Tour'}
              />
            </div>
          ) : (
            <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
              <p className="text-[var(--vibe-color-text-muted)] font-body">
                No virtual tours are available yet. Paste an embed URL to add one.
              </p>
            </div>
          )}
        </EditableSlotGroup>
      </div>

      <style>{`
        /* Style the tour selector to match the site's .comfy-cta buttons:
           strip the native chevron and draw our own caret that inherits the
           button's text color (so it flips with the secondary→primary hover). */
        .vt-tour-select__control {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          text-align: left;
          padding-left: 1.5rem;
          padding-right: 3rem;
        }
        .vt-tour-select__control::-ms-expand { display: none; }
        .vt-tour-select__control:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .vt-tour-select__caret {
          position: absolute;
          top: 50%;
          right: 1.35rem;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 6px solid var(--comfy-cta-fg, var(--vibe-color-text-on-secondary));
          pointer-events: none;
          transition: border-top-color 0.3s ease;
        }
        .vt-tour-select:hover .vt-tour-select__caret {
          border-top-color: var(--comfy-cta-fg-hover, var(--vibe-color-text-on-primary));
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  slotGroups: {
    'virtualtourembed.manual_tour': manualTourDef,
  },
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
